import { useQuery } from '@tanstack/react-query'

import request, { type ComplexResponseType } from './request'
import {
  zodSubjectGroupResponseValidator,
  zodSubjectResponseValidator,
  type SubjectGroupResponse,
  type SubjectResponse
} from './types/Subject'
import { useCurrentOrgId } from '@/stores/authStore'

export const NEEDS_INVITE_CODE = 'NEEDS_INVITE_CODE'

const getAccessibleSubjects = async ({
  page,
  limit,
  filters
}: {
  page: number,
  limit: number,
  filters?: Record<string, string>
}) => {
  const queryParams: string[] = []
  if (page > 1) queryParams.push('page=' + page)
  if (limit > 1) queryParams.push('limit=' + limit)
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value) queryParams.push(`${key}=${value}`)
  })
  const res = await request<ComplexResponseType<SubjectResponse[]>>({
    endpoint: `/subject?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.items?.length) zodSubjectResponseValidator(res.data.items)
  return res
}

export const useGetAccessibleSubjects = ({
  page, limit, filters
}: {
  page: number,
  limit: number,
  filters?: Record<string, string>
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'accessible_subjects', page, limit, filters],
    queryFn: () => getAccessibleSubjects({ page, limit, filters }),
    enabled
  })
}

const getPrivateSubject = async (userId: string) => {
  const res = await request<SubjectResponse>({
    endpoint: `/subject/${userId}`,
    method: 'GET'
  })
  if (res.success) zodSubjectResponseValidator([res.data])
  return res
}

export const useGetPrivateSubject = (userId: string | undefined, enabled: boolean) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'private_profile', userId],
    queryFn: () => getPrivateSubject(userId || ''),
    enabled: !!userId && enabled,
    retry: false,
  })
}

const getPublicSubject = async (userId: string) => {
  const res = await request<SubjectResponse>({
    endpoint: `/public/subject/${userId}`,
    method: 'GET'
  })
  if (res.success) zodSubjectResponseValidator([res.data])
  return res
}

export const useGetPublicSubject = (userId: string | undefined, enabled: boolean) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'public_profile', userId],
    queryFn: () => getPublicSubject(userId || ''),
    enabled: !!userId && enabled,
    retry: false,
  })
}

const getSubjectGroups = async (assigned?: boolean) => {
  const queryParams: string[] = []
  if (assigned !== undefined) {
    queryParams.push(`assigned=${assigned}`)
  }
  const res = await request<SubjectGroupResponse[]>({
    endpoint: `/subject/groups?${queryParams.join('&')}`,
    method: 'GET'
  })
  if (res.data?.length) zodSubjectGroupResponseValidator(res.data)
  return res
}

export const useGetSubjectGroups = ({
  assigned
}: {
  assigned?: boolean
}, enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'subject_groups', assigned],
    queryFn: () => getSubjectGroups(assigned),
    enabled,
  })
}

const getSubjectSpecialties = async () => {
  const res = await request<string[]>({
    endpoint: '/subject/specialties',
    method: 'GET'
  })
  return res
}

export const useGetSubjectSpecialties = (enabled = true) => {
  const currOrgId = useCurrentOrgId()
  return useQuery({
    queryKey: [currOrgId, 'subject_specialties'],
    queryFn: () => getSubjectSpecialties(),
    enabled,
  })
}
